/**
 * Prosjekter: hours per project, the basic version (owner, 2026-09-29), in the manager redesign's
 * one-page shape (2026-10-09): the company's projects with their hours in the month, then the same
 * month per project and person, with the CSV for the accountant. An hour total opens the matching
 * registrations in Arbeidstid; the filters live in the address so Back returns to the same view.
 * Only where the company's projects module is on and this release ships it (services/workspace.js);
 * otherwise the placeholder says why. Creating and changing a project is the Administrator's on the
 * server (server.js, authenticateToken(['admin'])), so the buttons follow is_admin.
 */
import { errorText, saveFile } from '@/api/client';
import {
  type Project,
  customersCsv,
  projectCsv,
  projectLabel,
  useBudgets,
  useCustomers,
  useProjectReport,
  useProjectWord,
  useProjects,
  useProjectsOn,
  useSaveProject,
} from '@/api/projects';
import { useCan } from '@/auth/caps';
import { T } from '@/i18n';
import { PowerOfficeCustomers } from '@/pages/lonn/PowerOfficeCustomers';
import { POWEROFFICE, TRIPLETEX } from '@/pages/lonn/payrollSystem';
import { today } from '@/rules';
import { useDirty } from '@/shell/context';
import { useNeutralSave } from '@/shell/leave';
import { useTenant } from '@/shell/tenant';
import { Button, Drawer, Empty, Menu, Panel, toast } from '@/ui';
import { PeriodControl } from '@/ui/PeriodControl';
import { monthLabel } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { PageHead, Status, initialsOf } from '@/ui/page';
import { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import './innstillinger/settings.css';
import { Bar, BudgetCard, ProjectNotes } from './prosjekter/Budget';
import { h1 } from './prosjekter/budgetAdvice';

// a figure the server left out reads as a dash, not a crash and not a made-up zero
const hrs = (n: number | null | undefined) =>
  typeof n === 'number' ? `${n.toLocaleString('nb-NO', { maximumFractionDigits: 2 })} t` : '–';
function monthOf(ym: string) {
  const [y, m] = ym.split('-').map(Number) as [number, number];
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return {
    from: `${ym}-01`,
    to: `${ym}-${String(last).padStart(2, '0')}`,
    label: monthLabel(`${y}-${String(m).padStart(2, '0')}`),
  };
}
const shift = (ym: string, by: number) => {
  const [y, m] = ym.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m - 1 + by, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

type StatusFilter = 'aktive' | 'avsluttede' | 'alle';
/** the project filter's value for a report group or a project: its id, or "uten" */
const keyOf = (id: number | null) => (id == null ? 'uten' : String(id));

export default function Prosjekter() {
  const { workspace } = useTenant();
  const on = useProjectsOn();
  const word = useProjectWord();
  if (!on) {
    const state = !workspace.available.projects ? 'unavailable' : 'disabled';
    return (
      <div className="pjpage">
        <PageHead
          title={word.many}
          sub={
            <span data-testid="projects-state">
              {state === 'unavailable'
                ? T('Ikke tilgjengelig i denne versjonen.')
                : T('Ikke i bruk for denne bedriften.')}
            </span>
          }
        />
        <Panel>
          <Empty>
            {T('Prosjektarbeid er ikke tilgjengelig i denne versjonen. Du kan fortsette å føre timer i')}{' '}
            <Link to="/timeliste">
              <Icon name="clock" />
              {T('Timelisten')}
            </Link>
            .
          </Empty>
        </Panel>
      </div>
    );
  }
  return <ProjectPage />;
}

function ProjectPage() {
  const can = useCan();
  const seeHours = can('timeliste.se');
  const { tenant } = useTenant();
  const word = useProjectWord();
  const admin = tenant?.me?.is_admin === true;
  const loc = useLocation();
  const [sp, setSp] = useSearchParams();
  const ym = /^\d{4}-\d{2}$/.test(sp.get('mnd') ?? '') ? (sp.get('mnd') as string) : today().slice(0, 7);
  const pick = sp.get('prosjekt') ?? 'alle';
  const status = (
    ['avsluttede', 'alle'].includes(sp.get('status') ?? '') ? sp.get('status') : 'aktive'
  ) as StatusFilter;
  const approvedOnly = sp.get('godkjent') !== '0';
  const set = (k: string, v: string | null) =>
    setSp(
      (p) => {
        if (v == null) p.delete(k);
        else p.set(k, v);
        return p;
      },
      { replace: true },
    );
  const m = monthOf(ym);
  const projectsQ = useProjects();
  const customersQ = useCustomers();
  const reportQ = useProjectReport(m.from, m.to, approvedOnly);
  const projects = projectsQ.data ?? [];
  const customers = customersQ.data ?? [];
  const report = seeHours ? reportQ.data : undefined;
  // the budget from the estimate (#132): beside each project, and in «Timer per kunde»
  const budgets = useBudgets().data?.projects ?? [];
  const budgetOf = (id: number | null) => budgets.find((b) => b.project_id === id && b.budget > 0);
  const picked = /^\d+$/.test(pick) ? Number(pick) : null;
  const budgetCell = (id: number) => {
    const b = budgetOf(id);
    if (!b) return '–';
    return (
      <span className="pjbcell">
        <span className="tnum">{T('{used} av {budget} t', { used: h1(b.used), budget: h1(b.budget) })}</span>
        <Bar
          percent={b.percent}
          warnAt={b.warn_percent}
          label={T('{p} prosent av budsjetterte timer brukt', { p: b.percent ?? 0 })}
        />
      </span>
    );
  };
  // «Timer per kunde» (#115) beside the project report, narrowed by the same project filter
  const customerGroups = (report?.customers ?? [])
    .map((c) => ({
      ...c,
      projects: c.projects.filter((p) => pick === 'alle' || pick === keyOf(p.project_id)),
    }))
    .filter((c) => pick === 'alle' || c.projects.length > 0);
  // ?vis=kunder|rapport (the welcome overview, #130): once the month's hours are in, scroll to «Timer per
  // kunde» or to the report table with the first «Timerapport» focused, then drop the word
  const goTo = sp.get('vis');
  // biome-ignore lint/correctness/useExhaustiveDependencies: once, when the report arrives
  useEffect(() => {
    if ((goTo !== 'kunder' && goTo !== 'rapport') || !report) return;
    const id = setTimeout(() => {
      document
        .getElementById(goTo === 'kunder' ? 'pj-kunder' : 'pj-rapport')
        ?.scrollIntoView?.({ block: 'start' });
      if (goTo === 'rapport') document.querySelector<HTMLElement>('[data-testid=pj-timerapport]')?.focus();
      set('vis', null);
    }, 0);
    return () => clearTimeout(id);
  }, [goTo, !!report]);
  // approved against waiting per project (decision 10), said only where something waits
  const waiting = (g: { approved?: number; waiting?: number }) =>
    g.waiting ? `${hrs(g.approved ?? 0)} ${T('godkjent')}, ${hrs(g.waiting)} ${T('venter')}` : null;

  const statusOk = (active: boolean) => status === 'alle' || active === (status === 'aktive');
  const shown = projects.filter((p) => statusOk(p.active) && (pick === 'alle' || pick === String(p.id)));
  const groups = (report?.projects ?? []).filter((g) => {
    if (pick !== 'alle' && pick !== keyOf(g.project_id)) return false;
    if (g.project_id == null) return status !== 'avsluttede';
    const p = projects.find((x) => x.id === g.project_id);
    return p ? statusOk(p.active) : status === 'alle';
  });
  const sum = Math.round(groups.reduce((a, g) => a + g.hours, 0) * 100) / 100;

  // the drill-through to the registrations behind a total, with the way back to this exact view
  const back = `${loc.pathname}${loc.search}`;
  const hourLink = (
    hours: number,
    g: {
      project_id: number | null;
      number: string | null;
      name: string | null;
    },
    person?: { user_id: number; name: string },
  ) => {
    if (!seeHours || typeof hours !== 'number' || hours === 0) return hrs(hours);
    const q = new URLSearchParams({
      periode: 'maned',
      mnd: ym,
      prosjekt: keyOf(g.project_id),
    });
    if (person) q.set('ansatt', String(person.user_id));
    if (approvedOnly) q.set('godkjent', '1');
    q.set('fra', back);
    const what = projectLabel(g) === 'Uten prosjekt' ? word.none : projectLabel(g);
    return (
      <Link
        className="mlink tnum"
        to={`/arbeidstid?${q.toString()}`}
        aria-label={`${hours.toLocaleString('nb-NO', { maximumFractionDigits: 2 })} ${T('timer i')} ${m.label}. ${T('Se registreringene for')} ${what}${person ? `, ${person.name}` : ''}`}
      >
        {hrs(hours)}
      </Link>
    );
  };
  const periodTotal = (p: Project) => {
    if (!seeHours) return '–';
    if (reportQ.isError) return '–';
    if (!report) return '…';
    const g = report.projects.find((x) => x.project_id === p.id);
    return hourLink(g?.hours ?? 0, {
      project_id: p.id,
      number: p.number,
      name: p.name,
    });
  };

  const [edit, setEdit] = useState<(Partial<Project> & { newCustomer?: string }) | null>(null);
  const [orig, setOrig] = useState<string>('');
  const save = useSaveProject();
  const dirty = !!edit && JSON.stringify(edit) !== orig;
  useDirty('project-edit', dirty, T('Prosjekt'));
  const submit = () =>
    new Promise<boolean>((done) => {
      if (!edit?.name?.trim()) return done(false);
      // the customer by id, or a typed name the server finds or creates (#115)
      const { newCustomer, customer, ...rest } = edit;
      const body =
        newCustomer != null
          ? {
              ...rest,
              customer: newCustomer.trim() || null,
              customer_id: undefined,
            }
          : { ...rest, customer_id: edit.customer_id ?? null };
      save.mutate(body as Partial<Project>, {
        onSuccess: () => {
          toast(T('Prosjektet er lagret'));
          setEdit(null);
          done(true);
        },
        onError: (e) => {
          toast(errorText(e));
          done(false);
        },
      });
    });
  useNeutralSave('project-edit', dirty ? submit : null);
  const startEdit = (p: Partial<Project>) => {
    setEdit(p);
    setOrig(JSON.stringify(p));
  };

  const download = async (which: 'prosjekt' | 'kunde' = 'prosjekt') => {
    try {
      const csv =
        which === 'kunde'
          ? await customersCsv(m.from, m.to, approvedOnly)
          : await projectCsv(m.from, m.to, approvedOnly);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      a.download = `${which === 'kunde' ? 'kundetimer' : 'prosjekttimer'}-${ym}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast(T('CSV lastet ned'));
    } catch (e) {
      toast(errorText(e));
    }
  };

  /**
   * The server builds the Excel and PDF files (#123): «Timer per prosjekt» and «Timer per kunde»
   * with the same lines as their CSV, and a project's customer report (timerapport) to mail on.
   */
  const period = `start_date=${m.from}&end_date=${m.to}&approved=${approvedOnly}`;
  const fetchFile = async (path: string, name: string) => {
    try {
      await saveFile(path, name);
      toast(T('{name} lastet ned', { name }));
    } catch (e) {
      toast(errorText(e));
    }
  };
  const reportFile = (which: 'report' | 'customers', ext: 'xlsx' | 'pdf') =>
    fetchFile(
      `/admin/projects/${which}.${ext}?${period}`,
      `${which === 'customers' ? 'kundetimer' : 'prosjekttimer'}-${ym}.${ext}`,
    );
  const fileButtons = (which: 'report' | 'customers', what: string) => (
    <>
      <span className="sp" />
      <Button
        small
        onClick={() => reportFile(which, 'xlsx')}
        aria-label={`${T('Last ned Excel')}, ${what}, ${m.label}`}
      >
        <Icon name="download" />
        {T('Last ned Excel')}
      </Button>
      <Button
        small
        onClick={() => reportFile(which, 'pdf')}
        aria-label={`${T('Last ned PDF')}, ${what}, ${m.label}`}
      >
        <Icon name="download" />
        {T('Last ned PDF')}
      </Button>
    </>
  );
  const customerReport = (g: {
    project_id: number | null;
    number: string | null;
    name: string | null;
  }) =>
    g.project_id == null ? null : (
      <Menu
        trigger={
          <button
            type="button"
            className="mlink pjrep"
            data-testid="pj-timerapport"
            aria-label={`${T('Timerapport til kunden')}, ${projectLabel(g)}, ${m.label}`}
          >
            <Icon name="document" />
            {T('Timerapport')}
          </button>
        }
        items={(['pdf', 'xlsx', 'csv'] as const).map((ext) => ({
          key: ext,
          label: ext === 'pdf' ? T('Som PDF') : ext === 'xlsx' ? T('Som Excel') : T('Som CSV'),
          onSelect: () =>
            fetchFile(
              `/admin/projects/${g.project_id}/hours.${ext}?${period}`,
              `timerapport-${g.number || g.name || g.project_id}-${ym}.${ext}`.replace(
                /[^\wæøåÆØÅ.-]+/g,
                '_',
              ),
            ),
        }))}
      />
    );

  return (
    <div className="pjpage">
      <PageHead
        title={word.many}
        sub={T('Arbeidstid per {what}, kunde og ansatt.', { what: word.lower })}
        actions={
          admin ? (
            <Button
              tone="pri"
              onClick={() =>
                startEdit({
                  name: '',
                  number: '',
                  customer_id: null,
                  active: true,
                })
              }
            >
              {T('Nytt {what}', { what: word.lower })}
            </Button>
          ) : undefined
        }
      />
      {/* a company on PowerOffice Go takes its customers from there (#183 phase 3) */}
      {admin && (
        <PowerOfficeCustomers sys={tenant?.payroll_system === 'tripletex' ? TRIPLETEX : POWEROFFICE} />
      )}
      <div className="mbar">
        <PeriodControl
          month
          value={ym}
          onChange={(v) => set('mnd', v)}
          prevLabel={T('Forrige måned')}
          nextLabel={T('Neste måned')}
          onPrev={() => set('mnd', shift(ym, -1))}
          onNext={() => set('mnd', shift(ym, 1))}
        >
          <span data-testid="report-month">{m.label}</span>
        </PeriodControl>
        <span className="sp" />
        <label className="mf">
          {word.one}
          <select
            className="inp"
            value={pick}
            onChange={(e) => set('prosjekt', e.target.value === 'alle' ? null : e.target.value)}
          >
            <option value="alle">{T('Alle')}</option>
            {projects.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {projectLabel(p)}
              </option>
            ))}
            <option value="uten">{word.none}</option>
          </select>
        </label>
        <label className="mf">
          {T('Status')}
          <select
            className="inp"
            value={status}
            onChange={(e) => set('status', e.target.value === 'aktive' ? null : e.target.value)}
          >
            <option value="aktive">{T('Aktive')}</option>
            <option value="avsluttede">{T('Avsluttede')}</option>
            <option value="alle">{T('Alle')}</option>
          </select>
        </label>
      </div>

      {seeHours && <ProjectNotes />}

      <section className="mcard">
        <div className="mch">
          <h2>{word.many}</h2>
        </div>
        {shown.length === 0 ? (
          <Empty>
            {projects.length === 0
              ? T('Ingen {what} ennå.', { what: word.many.toLowerCase() })
              : T('Ingen {what} i dette utvalget.', {
                  what: word.many.toLowerCase(),
                })}
          </Empty>
        ) : (
          <table className="mt cards" data-testid="projects-table">
            <thead>
              <tr>
                <th>{word.one}</th>
                <th>{T('Kunde')}</th>
                <th>{T('Status')}</th>
                <th className="num">{T('Timer i perioden')}</th>
                {seeHours && <th>{T('Budsjett')}</th>}
                {admin && <th aria-label={T('Handlinger')} />}
              </tr>
            </thead>
            <tbody>
              {shown.map((p) => (
                <tr key={p.id}>
                  <td>
                    <b>{p.name}</b>
                    <small>{p.number || T('Uten prosjektnummer')}</small>
                  </td>
                  <td data-k={T('Kunde')}>{p.customer || T('Ikke oppgitt')}</td>
                  <td>
                    <Status tone={p.active ? 'ok' : 'neu'}>{p.active ? T('Aktivt') : T('Avsluttet')}</Status>
                  </td>
                  <td className="num" data-k={T('Timer i perioden')}>
                    {periodTotal(p)}
                  </td>
                  {seeHours && <td data-k={T('Budsjett')}>{budgetCell(p.id)}</td>}
                  {admin && (
                    <td>
                      <button
                        type="button"
                        className="mlink"
                        aria-label={`${T('Endre')} ${p.name}`}
                        onClick={() => startEdit(p)}
                      >
                        {T('Endre')}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {seeHours && picked != null && <BudgetCard id={picked} admin={admin} />}

      {seeHours ? (
        <>
          <div className="mbar pjcsv">
            <label className="pjcheck">
              <input
                type="checkbox"
                checked={approvedOnly}
                onChange={(e) => set('godkjent', e.target.checked ? null : '0')}
              />
              {T('Bare godkjente timer')}
            </label>
            <span className="sp" />
            <Button
              onClick={() => download('prosjekt')}
              aria-label={`${T('Last ned CSV')}, ${m.label}, ${T('per {what}', { what: word.lower })}`}
            >
              <Icon name="download" />
              {T('Last ned CSV')}
            </Button>
            <Button
              onClick={() => download('kunde')}
              aria-label={`${T('Last ned CSV per kunde')}, ${m.label}`}
            >
              {T('CSV per kunde')}
            </Button>
          </div>
          <section className="mcard" id="pj-rapport">
            <div className="mch">
              <div>
                <h2>{T('Timer per {what} og ansatt', { what: word.lower })}</h2>
                <div className="sub">
                  {T('Tallene følger registreringene i Kontroll. Ufullstendige registreringer inngår ikke.')}
                </div>
              </div>
              {fileButtons('report', T('per {what}', { what: word.lower }))}
            </div>
            {reportQ.isError ? (
              <Empty>{errorText(reportQ.error)}</Empty>
            ) : !report ? (
              <Empty>{T('Laster …')}</Empty>
            ) : groups.length === 0 ? (
              <Empty>{T('Ingen stemplede timer i perioden.')}</Empty>
            ) : (
              <table className="mt cards pjreport" data-testid="report-table">
                <thead>
                  <tr>
                    <th>{T('{what} og ansatt', { what: word.one })}</th>
                    <th className="num">{T('Dager')}</th>
                    <th className="num">{T('Arbeidstid')}</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.flatMap((g) => [
                    <tr key={`p${keyOf(g.project_id)}`} className="pjgroup">
                      <td>
                        {g.name ? `${g.number ? `${g.number} · ` : ''}${g.name}` : word.none}
                        {g.customer ? <small>{g.customer}</small> : null}
                        {waiting(g) ? <small data-testid="pj-waiting">{waiting(g)}</small> : null}
                        {customerReport(g)}
                      </td>
                      <td className="num" data-k={T('Dager')}>
                        {g.days}
                      </td>
                      <td className="num" data-k={T('Arbeidstid')}>
                        {hourLink(g.hours, g)}
                      </td>
                    </tr>,
                    ...(g.people ?? []).map((u) => (
                      <tr key={`p${keyOf(g.project_id)}-u${u.user_id}`}>
                        <td>
                          <span className="mwho">
                            <span className="av" aria-hidden="true">
                              {initialsOf(u.name)}
                            </span>
                            <b>{u.name}</b>
                          </span>
                        </td>
                        <td className="num" data-k={T('Dager')}>
                          {u.days}
                        </td>
                        <td className="num" data-k={T('Arbeidstid')}>
                          {hourLink(u.hours, g, u)}
                        </td>
                      </tr>
                    )),
                  ])}
                </tbody>
              </table>
            )}
            {report && groups.length > 0 && (
              <div className="mcf">
                <b>{T('Sum i valgt utvalg')}</b>
                <span className="sp" />
                <b className="tnum" data-testid="report-total">
                  {hrs(sum)}
                </b>
              </div>
            )}
          </section>
          <section className="mcard" id="pj-kunder">
            <div className="mch">
              <div>
                <h2>{T('Timer per kunde')}</h2>
                <div className="sub">
                  {T('Samme måned og samme utvalg, summert per kunde og {what}.', { what: word.lower })}
                </div>
              </div>
              {fileButtons('customers', T('per kunde'))}
            </div>
            {!report ? null : customerGroups.length === 0 ? (
              <Empty>{T('Ingen stemplede timer i perioden.')}</Empty>
            ) : (
              <table className="mt cards pjreport" data-testid="customer-table">
                <thead>
                  <tr>
                    <th>{T('Kunde og {what}', { what: word.lower })}</th>
                    <th className="num">{T('Dager')}</th>
                    <th className="num">{T('Arbeidstid')}</th>
                  </tr>
                </thead>
                <tbody>
                  {customerGroups.flatMap((c) => [
                    <tr key={`c${c.customer_id ?? 'none'}`} className="pjgroup">
                      <td>
                        {c.name ?? T('Uten kunde')}
                        {waiting(c) ? <small>{waiting(c)}</small> : null}
                      </td>
                      <td className="num" data-k={T('Dager')}>
                        {c.days}
                      </td>
                      <td className="num" data-k={T('Arbeidstid')}>
                        {hrs(c.hours)}
                      </td>
                    </tr>,
                    ...c.projects.map((p) => (
                      <tr key={`c${c.customer_id ?? 'none'}-p${keyOf(p.project_id)}`}>
                        <td>
                          {p.name ? `${p.number ? `${p.number} · ` : ''}${p.name}` : word.none}
                          {budgetOf(p.project_id) && (
                            <small data-testid="pj-customer-budget">
                              {T('Budsjett {budget} t, brukt {used} t totalt', {
                                budget: h1(budgetOf(p.project_id)?.budget ?? 0),
                                used: h1(budgetOf(p.project_id)?.used ?? 0),
                              })}
                            </small>
                          )}
                        </td>
                        <td className="num" data-k={T('Dager')}>
                          {p.days}
                        </td>
                        <td className="num" data-k={T('Arbeidstid')}>
                          {hourLink(p.hours, p)}
                        </td>
                      </tr>
                    )),
                  ])}
                </tbody>
              </table>
            )}
          </section>
        </>
      ) : (
        <p className="mfoot">{T('Du har ikke tilgang til timene.')}</p>
      )}

      <Drawer
        open={!!edit}
        onClose={() => setEdit(null)}
        title={edit?.id ? T('Endre {what}', { what: word.lower }) : T('Nytt {what}', { what: word.lower })}
        footer={
          <>
            <span className="sp" />
            <Button onClick={() => setEdit(null)}>{T('Avbryt')}</Button>
            <Button tone="pri" onClick={submit} disabled={save.isPending || !edit?.name?.trim()}>
              {T('Lagre')}
            </Button>
          </>
        }
      >
        {edit && (
          <div style={{ display: 'grid', gap: 14 }}>
            <label className="fld">
              <span>{T('Navn')}</span>
              <input
                className="inp"
                value={edit.name ?? ''}
                onChange={(e) => setEdit({ ...edit, name: e.target.value })}
              />
            </label>
            <label className="fld">
              <span>{T('Prosjektnummer (valgfritt)')}</span>
              <input
                className="inp"
                value={edit.number ?? ''}
                onChange={(e) => setEdit({ ...edit, number: e.target.value })}
              />
            </label>
            <label className="fld">
              <span>{T('Kunde (valgfritt)')}</span>
              <select
                className="inp"
                data-testid="project-customer"
                value={edit.newCustomer != null ? 'ny' : (edit.customer_id ?? '')}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === 'ny') setEdit({ ...edit, newCustomer: '' });
                  else {
                    const { newCustomer: _n, ...rest } = edit;
                    setEdit({ ...rest, customer_id: v ? Number(v) : null });
                  }
                }}
              >
                <option value="">{T('Ingen kunde')}</option>
                {customers
                  .filter((c) => c.active || c.id === edit.customer_id)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                <option value="ny">{T('Ny kunde …')}</option>
              </select>
            </label>
            {edit.newCustomer != null && (
              <label className="fld">
                <span>{T('Kundens navn')}</span>
                <input
                  className="inp"
                  aria-label={T('Kundens navn')}
                  value={edit.newCustomer}
                  onChange={(e) => setEdit({ ...edit, newCustomer: e.target.value })}
                />
              </label>
            )}
            <label className="fld" style={{ gridTemplateColumns: 'auto 1fr', border: 0 }}>
              <input
                type="checkbox"
                checked={edit.active !== false}
                onChange={(e) => setEdit({ ...edit, active: e.target.checked })}
              />
              <span>{T('Aktiv: de ansatte kan velge det når de stempler inn')}</span>
            </label>
            <p className="hint">{T('Avsluttede beholder registrerte timer og vises med statusfilteret.')}</p>
          </div>
        )}
      </Drawer>
    </div>
  );
}
